import React from "react"
import { Form, Formik } from "formik"
import { Form as FormBootstrap } from "react-bootstrap"
import DebouncedValidatableInput from "../ValidatableInput/DebouncedValidatableInput"
import FormSection from "../FormHelpers/Section/FormSection"
import { useTranslation } from "react-i18next"
import { ButtonGroup } from "react-bootstrap"
import { testId } from "../../utility/tests/testId/dialogs"
import LoadingButton from "../LoadingButton/LoadingButton"
import { FirstKeys } from "./HotKeysExtensionForm.const"
import styles from "./HotKeysExtensionForm.module.scss"
import { nameof } from "../../utility/common/nameof"
import keycode from "keycode"
import { HotKey, hotKeyEmpty, HotKeyValues } from "../../models/fastRepliesHotKeys"
import { getHotKeysValidationSchema, tErrorNamespace } from "./helpers"
import { getLatinCharCode } from "../HotKeysExtension/helpers/toHotKeyValues"

interface HotKeysExtensionFormProps {
    id: string
    hidePopover: () => void
    handleSetHotKey: (hotKeyToSet: HotKey, rewrite: boolean) => void
    reset: () => void
    hotKey?: HotKeyValues
    loading?: boolean
    articleTitle?: string
}

const tNamespace = "dialogs:info.fast-replies.hot-keys.form."

const ARTICLE_TITLE_TAG = "__ARTICLE_TITLE__"

const HotKeysExtensionForm: React.FC<HotKeysExtensionFormProps> = props => {
    const { id, hidePopover, hotKey, handleSetHotKey, loading, articleTitle, reset } = props
    const { t } = useTranslation()

    const isArticleExist = !!articleTitle

    const handleSubmit = (values: HotKeyValues) => {
        const secondKeyCode = getLatinCharCode(values.SecondKey)

        if (!secondKeyCode) {
            return
        }

        handleSetHotKey(
            {
                FirstKey: keycode(values.FirstKey),
                SecondKey: secondKeyCode,
                CombinationName: `${values.FirstKey.toUpperCase()}+${values.SecondKey.toUpperCase()}`
            },
            isArticleExist
        )
    }

    return (
        <Formik
            initialValues={hotKey ?? hotKeyEmpty}
            onSubmit={handleSubmit}
            validationSchema={getHotKeysValidationSchema()}
        >
            {formikProps => {
                const isSubmitDisabled =
                    Object.keys(formikProps.errors).length > 0 || (!isArticleExist && !formikProps.dirty)

                return (
                    <Form onSubmit={formikProps.handleSubmit}>
                        <FormSection withoutPadding={isArticleExist}>
                            <FormBootstrap.Label className={styles.hotKeyForm__label}>
                                {t(`${tNamespace}firstKey`)}
                            </FormBootstrap.Label>
                            <DebouncedValidatableInput
                                id={`${id}-firstKey`}
                                name={nameof<HotKeyValues>("FirstKey")}
                                as="select"
                                disabled
                                className={styles.hotKeyForm__invalidFeedback}
                            >
                                <option value="" hidden>
                                    {t(`${tNamespace}chooseCombination`)}
                                </option>
                                {Object.entries(FirstKeys).map(([key, value]) => (
                                    <option value={key} key={value}>
                                        {key}
                                    </option>
                                ))}
                            </DebouncedValidatableInput>
                            <FormBootstrap.Label className={styles.hotKeyForm__label}>
                                {t(`${tNamespace}secondKey`)}
                            </FormBootstrap.Label>
                            <DebouncedValidatableInput
                                id={`${id}-secondKey`}
                                name={nameof<HotKeyValues>("SecondKey")}
                                placeholder={t(`${tNamespace}enterTextPlaceholder`)}
                                className={styles.hotKeyForm__invalidFeedback}
                            />
                        </FormSection>
                        {isArticleExist && (
                            <div className={styles.hotKeyForm__articleExistError}>
                                {t(`${tErrorNamespace}alreadyUsed`).replace(ARTICLE_TITLE_TAG, articleTitle)}
                            </div>
                        )}
                        <ButtonGroup className={styles.hotKeyForm__buttons}>
                            <LoadingButton
                                as="div"
                                variant="light"
                                onClick={isArticleExist ? reset : hidePopover}
                                testId={testId.cancelTransferDialogButton}
                                className={styles.hotKeyForm__buttons_cancel}
                            >
                                {isArticleExist ? t(`${tNamespace}no`) : t(`${tNamespace}cancel`)}
                            </LoadingButton>
                            <LoadingButton
                                as="button"
                                variant="primary"
                                type="submit"
                                testId={testId.dialogTransferModalSubmit}
                                loading={loading}
                                disabled={isSubmitDisabled}
                            >
                                {isArticleExist ? t(`${tNamespace}yes`) : t(`${tNamespace}save`)}
                            </LoadingButton>
                        </ButtonGroup>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default HotKeysExtensionForm
